/// <amd-module name="Wit/DeliveryPlans/NewPlanPanel/NewPlanPanel" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { ContentSize } from "VSSUI/Callout";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader, PanelOverlay } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { PlanType } from "Wit/Clients/Work/Contracts/Work";
import { getFieldCriteriaAsFilterClauses, ICriteriaService } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaService";
import { CriteriaSettingsView } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaSettingsView";
import {
    getTeamSettingsAsTeamBacklogMapping,
    IDeliveryPlansTeamSettingsService
} from "Wit/DeliveryPlans/Common/Components/TeamSettingsTable/DeliveryPlansTeamSettingsService";
import { TeamSettingsTable } from "Wit/DeliveryPlans/Common/Components/TeamSettingsTable/TeamSettingsTable";
import { IPlanService } from "Wit/DeliveryPlans/Common/Services/IPlanService";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { PlanNameAndDescriptionInput } from "../Common/Components/PlanNameAndDescriptionInput";
import { getPlanUrl } from "../Common/Utils/RoutingUtils";
import { publishTelemetry, TelemetryConstants } from "../Common/Utils/TelemetryUtils";

export class INewPlanPanelProps {
    onDismiss: () => void;
}

export class NewPlanPanel extends VssComponent<INewPlanPanelProps> {
    private _description: ObservableValue<string>;
    private _errorMessage: ObservableValue<string>;
    private _name: ObservableValue<string>;
    private _showOverlay: ObservableValue<boolean>;

    private _criteriaService: ICriteriaService;
    private _teamService: IDeliveryPlansTeamSettingsService;

    constructor(props: INewPlanPanelProps, context: IVssComponentContext) {
        super(props, context);

        this._errorMessage = new ObservableValue("");
        this._name = new ObservableValue("");
        this._description = new ObservableValue("");
        this._showOverlay = new ObservableValue(false);

        this._criteriaService = context.pageContext.getService<ICriteriaService>("ICriteriaService");
        this._teamService = context.pageContext.getService<IDeliveryPlansTeamSettingsService>("IDeliveryPlansTeamSettingsService");
    }

    public render(): JSX.Element {
        return (
            <Observer showOverlay={this._showOverlay}>
                {(props: { showOverlay: boolean }) => (
                    <CustomPanel
                        className="new-plan-panel"
                        lightDismiss={!props.showOverlay}
                        onDismiss={() => this._onDismiss()}
                        size={ContentSize.ExtraLarge}
                        defaultActiveElement=".name-text-field"
                    >
                        <PanelHeader
                            onDismiss={() => this._onDismiss()}
                            titleProps={{
                                iconProps: { iconName: "PlanView" },
                                text: Resources.NewDeliveryPlanLabel
                            }}
                        />
                        <PanelContent>
                            <div className="flex-column flex-grow rhythm-vertical-24">
                                <div className="padding-horizontal-20 rhythm-vertical-16">
                                    {Resources.WizardPlanHeader + " "}
                                    <Link
                                        aria-label={Resources.LearnMoreLinkLabel}
                                        href="https://go.microsoft.com/fwlink/?linkid=839004"
                                        target="_blank"
                                    >
                                        {Resources.LearnMoreLinkText}
                                    </Link>
                                    <Observer errorMessage={this._errorMessage}>
                                        {(props: { errorMessage: string | undefined }) =>
                                            props.errorMessage ? (
                                                <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>
                                            ) : null
                                        }
                                    </Observer>
                                </div>
                                <PlanNameAndDescriptionInput
                                    className="padding-horizontal-20 rhythm-vertical-16"
                                    description={this._description}
                                    name={this._name}
                                />
                                <TeamSettingsTable />
                                <CriteriaSettingsView className="delivery-plans-settings-table" />
                            </div>
                        </PanelContent>
                        <PanelFooter>
                            <ButtonGroup className="bolt-panel-footer-buttons">
                                <Button text={Resources.CancelButtonText} onClick={() => this._onDismiss()} />
                                <Observer
                                    name={this._name}
                                    teamSettingsError={this._teamService.validationErrorMessage}
                                    criteriaError={this._criteriaService.validationErrorMessage}
                                >
                                    {(props: { name: string; teamSettingsError: string | undefined; criteriaError: string | undefined }) => (
                                        <Button
                                            disabled={!props.name || props.teamSettingsError !== undefined || props.criteriaError !== undefined}
                                            onClick={this._onCreateClick}
                                            primary
                                            text={Resources.CreateButtonText}
                                        />
                                    )}
                                </Observer>
                            </ButtonGroup>
                        </PanelFooter>
                        {props.showOverlay && <PanelOverlay overlayContent={<Spinner label={Resources.CreatingPlan} />} />}
                    </CustomPanel>
                )}
            </Observer>
        );
    }

    /**
     * Clear all user entered data and close panel
     */
    private _onDismiss() {
        this._teamService.clear();
        this._criteriaService.clear();
        this.props.onDismiss();
    }

    private _onCreateClick = async () => {
        const pageContext = this.context.pageContext;
        const planService = pageContext.getService<IPlanService>("IPlanService");

        const planToCreate = {
            description: this._description.value,
            name: this._name.value,
            properties: {
                criteria: getFieldCriteriaAsFilterClauses(this._criteriaService.rows.value),
                teamBacklogMappings: getTeamSettingsAsTeamBacklogMapping(this._teamService.rows.value)
            },
            type: PlanType.DeliveryTimelineView
        };

        try {
            this._showOverlay.value = true;
            const createdPlan = await planService.createPlan(planToCreate);

            this._onDismiss();

            const properties: { [key: string]: any } = {
                [TelemetryConstants.TeamCount]: createdPlan.properties.teamBacklogMappings.length
            };
            publishTelemetry(this.context.pageContext, TelemetryConstants.PlanCreated, properties);

            const url = getPlanUrl(pageContext, createdPlan.id);
            onClickFPS(this.context.pageContext, url, true);
        } catch (ex) {
            this._showOverlay.value = false;
            this._errorMessage.value = ex.message;
        }
    };
}
